/**
 * ReportingPanel — Phreatic site Reporting tab.
 *
 * The persistent library of every PDF report (CARI-generated or
 * user-generated) for this site, plus the upgraded "Generate New"
 * entry point.
 *
 * The Generate New dropdown is the single entry point for every
 * deliverable available to the caller's capability profile. Reports
 * that need extra input (TCEQ Form 20982 → subdivision name + lot
 * count + tax parcel) open a modal; reports that don't (Banking,
 * Property Risk, Lender Groundwater Assessment, etc.) generate
 * inline against the current site.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  BeakerIcon,
  BuildingLibraryIcon,
  BuildingOffice2Icon,
  CalendarDaysIcon,
  ChevronDownIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
  GlobeAltIcon,
  PlusIcon,
  ShieldExclamationIcon,
  SparklesIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

import {
  GenerateReportRequest,
  ReportCatalogEntry,
  ReportProfileCatalog,
  ReportSummary,
  phreaticApi,
} from '../../api/phreatic';
import { TceqNewProjectModal } from '../../components/tceq/TceqNewProjectModal';

interface ReportingPanelProps {
  siteId: string;
}

// ── Per-report metadata ────────────────────────────────────────────
// Drives the upgraded dropdown's icon + tagline. Extend when a new
// spec ships — falls back to a generic DocumentTextIcon + the spec
// title if a code isn't in the table.
type ReportMeta = {
  icon: typeof DocumentTextIcon;
  tagline: string;
  /** Tailwind text color for the icon chip. Keep restrained — one
   *  highlight per spec, mostly cyan/emerald/violet for grouping. */
  accent: string;
  /** True for specs that need a project / extra input before
   *  generating (currently just TCEQ Form 20982). */
  needsProject?: boolean;
};

const REPORT_META: Record<string, ReportMeta> = {
  'phreatic.tceq_form_20982': {
    icon: ClipboardDocumentCheckIcon,
    tagline:
      'Auto-fill the official TCEQ form for platting. PE-signable; 9 pages + confidence summary.',
    accent: 'text-cyan-300',
    needsProject: true,
  },
  'phreatic.lender_groundwater_assessment': {
    icon: BuildingLibraryIcon,
    tagline:
      'Credit-committee deliverable with BLUF decision memo + Phase-I-style body + PG signature block.',
    accent: 'text-violet-300',
  },
  'phreatic.property_water_risk': {
    icon: ShieldExclamationIcon,
    tagline:
      'Quick-screen risk rating for pre-deal triage. ~6 pages, single-site scope.',
    accent: 'text-cyan-300',
  },
  'phreatic.groundwater_availability_study': {
    icon: BeakerIcon,
    tagline:
      'Talon-style 30-50 page GAS — precipitation, pumping rates, sat-thickness trend, Theis sims, well inventory.',
    accent: 'text-cyan-300',
  },
  'phreatic.statewide_aquifer_brief': {
    icon: GlobeAltIcon,
    tagline:
      'Aquifer-level health snapshot for agency staff. County or basin scale.',
    accent: 'text-sky-300',
  },
  'phreatic.municipal_supply_assessment': {
    icon: BuildingOffice2Icon,
    tagline:
      'Source-water yield + conservation triggers + supply-vs-demand horizon for utilities.',
    accent: 'text-cyan-300',
  },
  'phreatic.operator_compliance_calendar': {
    icon: CalendarDaysIcon,
    tagline:
      'Rolling 12-month deadlines: monitoring, permit renewals, GCD reports.',
    accent: 'text-cyan-300',
  },
};

const FALLBACK_META: ReportMeta = {
  icon: DocumentTextIcon,
  tagline: 'Phreatic deliverable.',
  accent: 'text-mono-200',
};

function metaFor(code: string): ReportMeta {
  return REPORT_META[code] || FALLBACK_META;
}

// ── Library row helpers ────────────────────────────────────────────

const STATUS_BADGE: Record<ReportSummary['status'], string> = {
  queued: 'bg-mono-700 text-mono-200',
  running: 'bg-cyan-500/20 text-cyan-300',
  completed: 'bg-cyan-500/20 text-cyan-300',
  failed: 'bg-red-500/20 text-red-300',
};

function formatBytes(n: number | null): string {
  if (n == null) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}


export function ReportingPanel({ siteId }: ReportingPanelProps) {
  const [reports, setReports] = useState<ReportSummary[]>([]);
  const [availableCodes, setAvailableCodes] = useState<string[]>([]);
  const [catalog, setCatalog] = useState<ReportProfileCatalog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showGenerate, setShowGenerate] = useState(false);
  const [generating, setGenerating] = useState<string | null>(null);
  const [showTceqModal, setShowTceqModal] = useState(false);

  // Outside-click dismiss for the dropdown.
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!showGenerate) return;
    function onClick(e: MouseEvent) {
      if (
        dropdownRef.current
        && !dropdownRef.current.contains(e.target as Node)
      ) {
        setShowGenerate(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setShowGenerate(false);
    }
    window.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [showGenerate]);

  const loadReports = useCallback(async () => {
    try {
      const res = await phreaticApi.listSiteReports(siteId);
      setReports(res.reports);
      setAvailableCodes(res.available_codes);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  const loadCatalog = useCallback(async () => {
    try {
      const res = await phreaticApi.getReportProfileCatalog();
      setCatalog(res);
    } catch {
      // non-fatal — dropdown just stays empty
    }
  }, []);

  useEffect(() => {
    loadReports();
    loadCatalog();
  }, [loadReports, loadCatalog]);

  // Poll while any row is in-flight.
  useEffect(() => {
    const inFlight = reports.some(
      (r) => r.status === 'queued' || r.status === 'running',
    );
    if (!inFlight) return;
    const handle = window.setInterval(loadReports, 2000);
    return () => window.clearInterval(handle);
  }, [reports, loadReports]);

  async function handleGenerate(entry: ReportCatalogEntry) {
    const meta = metaFor(entry.report_code);
    setShowGenerate(false);
    // TCEQ needs a project-creation modal first; everything else
    // generates straight against the current site.
    if (meta.needsProject) {
      setShowTceqModal(true);
      return;
    }
    setGenerating(entry.title);
    try {
      const body: GenerateReportRequest = {
        report_code: entry.report_code,
        params: { site_id: siteId },
      };
      await phreaticApi.generateSiteReport(siteId, body);
      await loadReports();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Generation failed');
    } finally {
      setGenerating(null);
    }
  }

  async function handleView(report: ReportSummary) {
    if (report.status !== 'completed') return;
    try {
      const res = await phreaticApi.getReportDownloadUrl(
        report.report_id, 3600, 'inline',
      );
      window.open(res.download_url, '_blank', 'noopener,noreferrer');
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to open report');
    }
  }

  async function handleDownload(report: ReportSummary) {
    try {
      const res = await phreaticApi.getReportDownloadUrl(
        report.report_id, 3600, 'attachment',
      );
      window.open(res.download_url, '_blank', 'noopener,noreferrer');
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Download failed');
    }
  }

  async function handleRegenerate(report: ReportSummary) {
    try {
      await phreaticApi.regenerateReport(report.report_id);
      await loadReports();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Regenerate failed');
    }
  }

  async function handleDelete(report: ReportSummary) {
    if (!window.confirm(`Delete "${report.report_title}"?`)) return;
    try {
      await phreaticApi.deleteReport(report.report_id);
      await loadReports();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Delete failed');
    }
  }

  // Dropdown content: catalog filtered to codes available for this caller.
  const generateOptions: ReportCatalogEntry[] = useMemo(() => {
    if (!catalog) return [];
    return catalog.reports.filter((r) => availableCodes.includes(r.report_code));
  }, [catalog, availableCodes]);

  const dropdownDisabled = generating !== null || generateOptions.length === 0;

  return (
    <div className="space-y-4 p-1">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-medium text-mono-100">
            Reporting Library
          </h3>
          <p className="text-xs text-mono-400 mt-0.5">
            Every report generated for this site. Download links refresh on demand.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={loadReports}
            className="p-2 rounded-md text-mono-300 hover:text-mono-100 hover:bg-mono-800 transition-colors"
            title="Refresh"
          >
            <ArrowPathIcon className="w-4 h-4" />
          </button>

          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowGenerate((v) => !v)}
              disabled={dropdownDisabled}
              className={
                "group inline-flex items-center gap-2 px-4 py-2 rounded-md "
                + "bg-gradient-to-b from-cyan-500/25 to-cyan-500/10 "
                + "hover:from-cyan-500/35 hover:to-cyan-500/15 "
                + "text-cyan-100 text-sm font-medium "
                + "border border-cyan-500/40 "
                + "shadow-[0_0_0_0_rgba(34,211,238,0)] hover:shadow-[0_0_18px_-2px_rgba(34,211,238,0.4)] "
                + "transition-all duration-200 "
                + "disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:shadow-none"
              }
            >
              <PlusIcon className="w-4 h-4" />
              <span>Generate New</span>
              <ChevronDownIcon
                className={
                  "w-3.5 h-3.5 transition-transform duration-200 "
                  + (showGenerate ? "rotate-180" : "")
                }
              />
            </button>

            {showGenerate && generateOptions.length > 0 && (
              <div
                className={
                  "absolute right-0 mt-2 w-[28rem] z-20 "
                  + "bg-mono-950/95 backdrop-blur-md "
                  + "border border-mono-700/80 rounded-lg "
                  + "shadow-2xl shadow-black/60 "
                  + "origin-top-right animate-fade-in"
                }
                role="menu"
              >
                <div className="flex items-center justify-between px-4 py-3 border-b border-mono-800">
                  <div className="flex items-center gap-2">
                    <SparklesIcon className="w-3.5 h-3.5 text-cyan-300" />
                    <span className="text-xs uppercase tracking-wide text-mono-400">
                      Generate a new report
                    </span>
                  </div>
                  <span className="text-[10px] text-mono-500">
                    Profile:{' '}
                    <span className="text-cyan-200">
                      {catalog?.profile || '—'}
                    </span>
                  </span>
                </div>
                <div className="py-1 max-h-[26rem] overflow-y-auto">
                  {generateOptions.map((opt) => {
                    const meta = metaFor(opt.report_code);
                    const Icon = meta.icon;
                    return (
                      <button
                        key={opt.report_code}
                        onClick={() => handleGenerate(opt)}
                        role="menuitem"
                        className={
                          "group w-full text-left px-4 py-3 "
                          + "flex items-start gap-3 "
                          + "hover:bg-mono-800/70 "
                          + "transition-colors duration-150 "
                          + "border-l-2 border-transparent "
                          + "hover:border-cyan-400/70"
                        }
                      >
                        <div
                          className={
                            "flex-shrink-0 w-9 h-9 rounded-md "
                            + "bg-mono-900 border border-mono-800 "
                            + "flex items-center justify-center "
                            + "group-hover:scale-105 transition-transform "
                            + meta.accent
                          }
                        >
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <div className="text-sm font-medium text-mono-100 truncate">
                              {opt.title}
                            </div>
                            <span className="flex-shrink-0 text-[10px] text-mono-500 uppercase tracking-wide">
                              {opt.section_count} sec
                            </span>
                          </div>
                          <div className="text-xs text-mono-400 leading-snug mt-0.5">
                            {meta.tagline}
                          </div>
                          {meta.needsProject && (
                            <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-cyan-300/90">
                              <span className="w-1 h-1 rounded-full bg-cyan-300/90" />
                              Opens project intake (3 fields)
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-md p-3 flex items-start gap-2">
          <ExclamationCircleIcon className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
          <div className="text-red-300 text-sm flex-1">{error}</div>
          <button
            onClick={() => setError(null)}
            className="text-red-300/70 hover:text-red-200 text-xs"
          >
            Dismiss
          </button>
        </div>
      )}

      {generating && (
        <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-md p-3 text-cyan-200 text-sm flex items-center gap-2">
          <ArrowPathIcon className="w-4 h-4 animate-spin" />
          Generating <b>{generating}</b> — this can take 30-90 seconds depending on the spec.
        </div>
      )}

      {/* Table */}
      <div className="border border-mono-800 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-mono-900 text-mono-300 text-xs">
              <th className="text-left px-3 py-2 font-medium">Title</th>
              <th className="text-left px-3 py-2 font-medium">Generated</th>
              <th className="text-left px-3 py-2 font-medium">Via</th>
              <th className="text-right px-3 py-2 font-medium">Pages</th>
              <th className="text-right px-3 py-2 font-medium">Size</th>
              <th className="text-left px-3 py-2 font-medium">Status</th>
              <th className="text-right px-3 py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-mono-500">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && reports.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-mono-500">
                  No reports generated yet. Use "Generate New" or ask CARI.
                </td>
              </tr>
            )}
            {reports.map((r) => (
              <tr
                key={r.report_id}
                onDoubleClick={() => handleView(r)}
                className={
                  "border-t border-mono-800 hover:bg-mono-900/50"
                  + (r.status === 'completed' ? " cursor-pointer" : "")
                }
                title={r.status === 'completed' ? 'Double-click to open in a new tab' : undefined}
              >
                <td className="px-3 py-2.5 text-mono-100">
                  <div className="font-medium">{r.report_title}</div>
                  {r.parent_report_id && (
                    <div className="text-xs text-mono-500">↻ regeneration</div>
                  )}
                  {r.status === 'failed' && r.error && (
                    <div className="text-xs text-red-400 mt-0.5 truncate max-w-md">
                      {r.error}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2.5 text-mono-300 text-xs">
                  {formatWhen(r.completed_at || r.requested_at)}
                </td>
                <td className="px-3 py-2.5 text-mono-400 text-xs">
                  {r.generated_via}
                </td>
                <td className="px-3 py-2.5 text-mono-300 text-right">
                  {r.page_count ?? '—'}
                </td>
                <td className="px-3 py-2.5 text-mono-300 text-right">
                  {formatBytes(r.size_bytes)}
                </td>
                <td className="px-3 py-2.5">
                  <span
                    className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${STATUS_BADGE[r.status]}`}
                  >
                    {r.status}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-right">
                  <div className="inline-flex items-center gap-1">
                    {r.status === 'completed' && (
                      <button
                        onClick={() => handleDownload(r)}
                        className="p-1.5 rounded text-mono-300 hover:text-cyan-200 hover:bg-mono-800 transition-colors"
                        title="Download"
                      >
                        <ArrowDownTrayIcon className="w-4 h-4" />
                      </button>
                    )}
                    {(r.status === 'completed' || r.status === 'failed') && (
                      <button
                        onClick={() => handleRegenerate(r)}
                        className="p-1.5 rounded text-mono-300 hover:text-cyan-200 hover:bg-mono-800 transition-colors"
                        title="Regenerate with fresh data"
                      >
                        <ArrowPathIcon className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(r)}
                      className="p-1.5 rounded text-mono-300 hover:text-red-300 hover:bg-mono-800 transition-colors"
                      title="Delete"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {availableCodes.length === 0 && !loading && (
        <div className="text-xs text-mono-500 italic">
          Your capability profile has no reports configured. Contact an admin to
          enable report generation for your profile.
        </div>
      )}

      {showTceqModal && (
        <TceqNewProjectModal
          siteId={siteId}
          onClose={() => setShowTceqModal(false)}
          onCreated={async () => {
            setShowTceqModal(false);
            await loadReports();
          }}
        />
      )}
    </div>
  );
}

export default ReportingPanel;
